import {
    WB_MessageRow,
    WB_MessageThreadRow,
    WB_MessageToRow,
    WB_UIAppExecutableRow, WB_UserGroupRow,
    WB_UserRow
} from "../PAdbSchemaRows";
import { PAdbContext } from "../PAdbContext";
import * as _ from "lodash"
import {
    esWB_Message, esWB_MessageThread, esWB_MessageTo,
    esWB_UI_AppExecutable,
    esWB_User, esWB_User_Group
} from "../PAdbSchema";
import { PAEnvironment } from "../PAEnvironment";
import { IUsrContext } from "../PA.Core.IF.User";
import * as AsyncLock from "async-lock";
import { PASearch } from "../PASearch";

const vUI_AppExec_Objects_schema = PASearch.createSimpleSchema(
    ['APPEXEC_ID','APPEXEC_NAME','ID_UI','NAME'],
    ['WB_UI_APPEXEC_ID','WB_ID','PARENT_WB_ID','WB_MDSET_ID','WB_SALESORG_ID']
)

export interface vUI_AppExec_Objects_Row {
    WB_UI_APPEXEC_ID: number
    APPEXEC_ID: string
    APPEXEC_NAME: string
    WB_ID: number
    ID_UI: string
    NAME: string
    PARENT_WB_ID: number
    WB_MDSET_ID: number
    WB_SALESORG_ID: number
}

export class UserMessageUtils {
    static dctUsersByScopeKey: {[scopeKey: string]: WB_UserRow[]} = {}
    static dctUsrGroupsByScopeKey: {[scopeKey: string]: WB_UserGroupRow[]} = {}
    static dctLock = new AsyncLock()

    static async createThread(sourceType: string, sourceWbId: number,
                              subject: string, messageText: string,
                              options?: {
                                  dbContext?: PAdbContext,
                                  appExecId?: string,
                                  targetWbId?: number,
                                  toUserWbIds?: number[],
                                  toUserGroupWbIds?: number[],
                              }, bSave: boolean = true): Promise<WB_MessageThreadRow>
    {
        let opts = options || {}
        let dbContext = options?.dbContext
        if(_.isEmpty(dbContext))
            dbContext = new PAdbContext()

        let usrRow = await UserMessageUtils.getCurrentUser(dbContext)
        if(_.isEmpty(usrRow))
            return undefined

        let thread = <WB_MessageThreadRow> await dbContext.createRecordWithKey(esWB_MessageThread)
        thread.setDefaults()
        thread.cif.THREAD_STATUS = "Open"
        thread.cif.THREAD_NAME = subject
        thread.cif.THREAD_TYPE = "Information"
        thread.cif.SOURCE_TYPE = sourceType
        thread.cif.SOURCE_WB_ID = sourceWbId
        thread.cif.APPEXEC_ID_TARGET = opts.appExecId || ""
        thread.cif.WB_ID_TARGET = opts.targetWbId || 0
        thread.cif.WB_AUTHUSR_ID = usrRow.cif.WB_AUTHUSR_ID
        thread.WB_UserRow = usrRow

        // Find AppExec
        let appExecRow = await UserMessageUtils.getAppExec(dbContext, thread.cif.APPEXEC_ID_TARGET)
        if(_.isEmpty(appExecRow)){
            thread.cif.WB_UI_APPEXEC_ID_TARGET = 0
        } else {
            thread.cif.WB_UI_APPEXEC_ID_TARGET = appExecRow.cif.WB_UI_APPEXEC_ID
            thread.WB_UIAppExecutableRow = appExecRow
        }
        dbContext.add(esWB_MessageThread, thread)

        // Create Message
        let msgOptions: any = {pin: true}
        if(!_.isEmpty(opts.toUserWbIds))
            msgOptions.toUserWbIds = opts.toUserWbIds
        let childMsg = await UserMessageUtils.createMessage(thread,messageText, msgOptions)

        // Save Thread and Messages
        if (bSave)
            await UserMessageUtils.save(dbContext, thread, childMsg)

        return thread
    }

    static async save(dbContext: PAdbContext,
                      threads?: WB_MessageThreadRow[] | WB_MessageThreadRow,
                      messages?: WB_MessageRow[] | WB_MessageRow): Promise<void>
    {
        let arrThreads: WB_MessageThreadRow[] = []
        let arrMessages: WB_MessageRow[] = []

        if(_.isArray(threads))
            arrThreads = threads
        else if(!_.isEmpty(threads))
            arrThreads = [threads]

        if(_.isArray(messages))
            arrMessages = messages
        else if (!_.isEmpty(messages))
            arrMessages = [messages]

        if(_.isEmpty(arrThreads) && _.isEmpty(arrMessages))
            return

        dbContext.BatchStart()
        if(!_.isEmpty(arrThreads))
            await dbContext.SaveBatch(esWB_MessageThread, arrThreads)
        if(!_.isEmpty(arrMessages)) {
            await dbContext.SaveBatch(esWB_Message, arrMessages)
            await dbContext.SaveBatch(esWB_MessageTo, _.flatMap(arrMessages, (m) => m.WB_MessageToRows))
        }

        await dbContext.BatchComplete()
    }

    static async closeThread(thread: WB_MessageThreadRow): Promise<void>
    {
        if(_.isEmpty(thread))
            return
        let dbContext = <PAdbContext> thread.dataStore()
        thread.cif.THREAD_STATUS = "Closed"
        await UserMessageUtils.save(dbContext, thread)
    }

    static async reopenThread(thread: WB_MessageThreadRow): Promise<void>
    {
        if(_.isEmpty(thread))
            return
        let dbContext = <PAdbContext> thread.dataStore()
        thread.cif.THREAD_STATUS = "Open"
        await UserMessageUtils.save(dbContext, thread)
    }

    static async createMessage(thread: WB_MessageThreadRow,
                               messageText: string,
                               options?: {
                                   replyTo?: WB_MessageRow,
                                   replyToCopyTo?: boolean,
                                   fromWbUserId?: number,
                                   toUserWbIds?: number[],
                                   toUserGroupWbIds?: number[],
                                   pin?: boolean
                               }, bSave: boolean = true): Promise<WB_MessageRow>
    {
        let opts = options || {}
        if(_.isEmpty(thread))
            return

        let dbContext = <PAdbContext> thread.dataStore()

        if(_.isEmpty(dbContext))
            return

        let fromUsr: WB_UserRow
        if(_.isNumber(opts.fromWbUserId))
            fromUsr = await UserMessageUtils.getUser(dbContext, opts.fromWbUserId)
        else
            fromUsr = await UserMessageUtils.getCurrentUser(dbContext)

        if(_.isEmpty(fromUsr))
            return

        let msg = <WB_MessageRow>await dbContext.createRecordWithKey(esWB_Message)
        msg.setDefaults()
        msg.cif.WB_MESSAGETHREAD_ID = thread.cif.WB_MESSAGETHREAD_ID
        msg.WB_MessageThreadRow = thread
        msg.cif.WB_AUTHUSR_ID = fromUsr.cif.WB_AUTHUSR_ID
        msg.WB_UserRow = fromUsr
        msg.cif.MESSAGE_TEXT = messageText
        if(!_.isEmpty(opts.replyTo))
            msg.cif.WB_MESSAGE_ID_REPLYTO = opts.replyTo.cif.WB_MESSAGE_ID
        if(opts.pin)
            msg.cif.PIN_IND = '1'

        dbContext.add(esWB_Message, msg)

        // Add To Field
        for(let wbUsrId of (opts.toUserWbIds || [])){
            await UserMessageUtils.addToUser(msg, wbUsrId, false)
        }

        if(bSave)
            await UserMessageUtils.save(dbContext, [], msg)

        return msg
    }

    static async addToUser(message: WB_MessageRow, wbUserId: number, bSave: boolean = true): Promise<WB_MessageToRow>
    {
        if(_.isEmpty(message))
            return

        let dbContext = <PAdbContext> message.dataStore()
        if(_.isEmpty(dbContext))
            return

        // If already exists, return existing
        for(let mt of message.WB_MessageToRows) {
            if(!mt.isActive)
                continue
            if(mt.cif.WB_AUTHUSR_ID == wbUserId)
                return mt
        }

        let toUsr = await UserMessageUtils.getUser(dbContext, wbUserId)
        if(_.isEmpty(toUsr))
            return

        // Create To Row
        let toRow = <WB_MessageToRow> await dbContext.createRecordWithKey(esWB_MessageTo)
        toRow.setDefaults()
        toRow.cif.TO_TYPE = "User"
        toRow.cif.WB_MESSAGE_ID = message.cif.WB_MESSAGE_ID
        toRow.WB_MessageRow = message
        toRow.cif.WB_AUTHUSR_ID = toUsr.cif.WB_AUTHUSR_ID
        toRow.WB_UserRow = toUsr
        dbContext.add(esWB_MessageTo, toRow)

        if(bSave)
            await dbContext.Save(esWB_MessageTo, toRow)
    }

    static async removeToUser(message: WB_MessageRow, wbUserId: number, bSave: boolean = true): Promise<void>
    {
        if(_.isEmpty(message))
            return

        let dbContext = <PAdbContext> message.dataStore()
        if(_.isEmpty(dbContext))
            return

        let arrChangeItems: WB_MessageToRow[] = []
        _.forEach(message.WB_MessageToRows, (mt) => {
            if(!mt.isActive)
                return
            if(mt.cif.WB_AUTHUSR_ID == wbUserId){
                mt.markDeleted()
                arrChangeItems.push(mt)
            }
        })

        if(bSave)
            await dbContext.Save(esWB_MessageTo, arrChangeItems)
    }

    static async addToUserGroup(message: WB_MessageRow, wbUsrGrpId: number, bSave: boolean = true): Promise<WB_MessageToRow>
    {
        if(_.isEmpty(message))
            return

        let dbContext = <PAdbContext> message.dataStore()
        if(_.isEmpty(dbContext))
            return

        // If already exists, return existing
        for(let mt of message.WB_MessageToRows) {
            if(!mt.isActive)
                continue
            if(mt.cif.WB_USRGRP_ID_ID == wbUsrGrpId)
                return mt
        }

        let toUsrGrp = await UserMessageUtils.getUserGroup(dbContext, wbUsrGrpId)
        if(_.isEmpty(toUsrGrp))
            return

        // Create To Row
        let toRow = <WB_MessageToRow> await dbContext.createRecordWithKey(esWB_MessageTo)
        toRow.setDefaults()
        toRow.cif.TO_TYPE = "UserGroup"
        toRow.cif.WB_MESSAGE_ID = message.cif.WB_MESSAGE_ID
        toRow.WB_MessageRow = message
        toRow.cif.WB_USRGRP_ID = toUsrGrp.cif.WB_USRGRP_ID
        toRow.WB_UserGroupRow = toUsrGrp
        dbContext.add(esWB_MessageTo, toRow)

        if(bSave)
            await dbContext.Save(esWB_MessageTo, toRow)
    }

    static async removeToUserGroup(message: WB_MessageRow, wbUsrGrpId: number, bSave: boolean = true): Promise<void>
    {
        if(_.isEmpty(message))
            return

        let dbContext = <PAdbContext> message.dataStore()
        if(_.isEmpty(dbContext))
            return

        let arrChangeItems: WB_MessageToRow[] = []
        _.forEach(message.WB_MessageToRows, (mt) => {
            if(!mt.isActive)
                return
            if(mt.cif.WB_USRGRP_ID == wbUsrGrpId){
                mt.markDeleted()
                arrChangeItems.push(mt)
            }
        })

        if(bSave)
            await dbContext.Save(esWB_MessageTo, arrChangeItems)
    }


    static async deleteMessage(message: WB_MessageRow, bSave: boolean = true): Promise<void> {

        if(_.isEmpty(message))
            return

        let dbContext = <PAdbContext> message.dataStore()
        if(_.isEmpty(dbContext))
            return

        message.markDeleted()
        _.forEach(message.WB_MessageToRows, (mt) => {
            mt.markDeleted()
        })

        if(bSave){
            dbContext.BatchStart()
            await dbContext.SaveBatch(esWB_Message, message)
            await dbContext.SaveBatch(esWB_MessageTo, message.WB_MessageToRows)
            await dbContext.BatchComplete()
        }

    }

    static async getUserList(options?: {usrRoleIds?: string[],
                                        usrWbIds?: number[]}): Promise<WB_UserRow[]>
    {

        let scope: string[] = []
        let wbUserIds: number[] = []

        if(!_.isEmpty(options?.usrRoleIds)) {
            let whereFieldSpecJSON = PASearch.createSimpleSchema(
                ['ROLE_ID'],['WB_AUTHUSR_ID','WB_CUST_ID', 'WB_USRGRP_ID'])
            scope.push('UsrRole:' + options.usrRoleIds.join('-'))
            let rows = await PASearch.find("vUI_UserRoles_All",
                {ROLE_ID: {inq: options?.usrRoleIds}},
                whereFieldSpecJSON,[],0,['WB_AUTHUSR_ID'])
            if(_.isEmpty(rows))
                wbUserIds = [-1]
            else
                wbUserIds = _.map(rows, (r) => + r.WB_AUTHUSR_ID)
        }
        if(!_.isEmpty(options?.usrWbIds)) {
            scope.push('Users:' + options.usrWbIds.join('-'))
            wbUserIds = _.union(wbUserIds, options.usrWbIds)
        }
        let key = "AllDefault"
        if(_.isEmpty(scope)) {
            key = scope.join(";")
        }

        return await UserMessageUtils.dctLock.acquire<WB_UserRow[]>(key, async (done) => {
            if(key in UserMessageUtils.dctUsersByScopeKey){
                done(undefined, UserMessageUtils.dctUsersByScopeKey[key])
                return
            }

            let dbContext = new PAdbContext()
            let cnd: any = {}
            if(!_.isEmpty(wbUserIds))
                cnd.WB_AUTHUSR_ID = {inq: wbUserIds};

            let rv = <WB_UserRow[]> await dbContext.findAll(esWB_User, {
                where: cnd
            })
            UserMessageUtils.dctUsersByScopeKey[key] = rv
            done(undefined, rv)
        })
    }

    static async getUserGroupList(options?: {usrRoleIds?: string[],
                                             usrGrpWbIds?: number[]}): Promise<WB_UserGroupRow[]>
    {

        let scope: string[] = []
        let wbUserGroupIds: number[] = []

        if(!_.isEmpty(options?.usrRoleIds)) {
            let whereFieldSpecJSON = PASearch.createSimpleSchema(
                ['ROLE_ID'],['WB_AUTHUSR_ID','WB_CUST_ID', 'WB_USRGRP_ID'])
            scope.push('UsrRole:' + options.usrRoleIds.join('-'))
            let rows = await PASearch.find("vUI_UserRoles_All",
                {ROLE_ID: {inq: options?.usrRoleIds}},
                whereFieldSpecJSON,[],0,['WB_USRGRP_ID'])
            if(_.isEmpty(rows))
                wbUserGroupIds = [-1]
            else
                wbUserGroupIds = _.map(rows, (r) => + r.WB_USRGRP_ID)
        }
        if(!_.isEmpty(options?.usrGrpWbIds)) {
            scope.push('UserGroups:' + options.usrGrpWbIds.join('-'))
            wbUserGroupIds = _.union(wbUserGroupIds, options.usrGrpWbIds)
        }
        let key = "AllDefault"
        if(_.isEmpty(scope)) {
            key = scope.join(";")
        }

        return await UserMessageUtils.dctLock.acquire<WB_UserGroupRow[]>(key, async (done) => {
            if(key in UserMessageUtils.dctUsrGroupsByScopeKey){
                done(undefined, UserMessageUtils.dctUsrGroupsByScopeKey[key])
                return
            }

            let dbContext = new PAdbContext()
            let cnd: any = {}
            if(!_.isEmpty(wbUserGroupIds))
                cnd.WB_USRGRP_ID = {inq: wbUserGroupIds};

            let rv = <WB_UserGroupRow[]> await dbContext.findAll(esWB_User_Group, {
                where: cnd
            })
            UserMessageUtils.dctUsrGroupsByScopeKey[key] = rv
            done(undefined, rv)
        })
    }

    static async getCurrentUser(dbContext: PAdbContext): Promise<WB_UserRow> {
        let usrCtx = <IUsrContext> PAEnvironment.getValue(PAEnvironment.UserContext)
        if(_.isEmpty(usrCtx?.User))
            return undefined

        let wbUsrId = usrCtx.User.cif.WB_AUTHUSR_ID
        return UserMessageUtils.getUser(dbContext, wbUsrId)
    }

    static async getUser(dbContext: PAdbContext, wbUsrId: number): Promise<WB_UserRow> {

        const usrs = <WB_UserRow[]> await dbContext.findUsingCache(esWB_User, [wbUsrId])
        return _.first(usrs)
    }

    static async getUserGroup(dbContext: PAdbContext, wbUsrGrpId: number): Promise<WB_UserGroupRow> {

        const usrGrps = <WB_UserGroupRow[]> await dbContext.findUsingCache(esWB_User_Group, [wbUsrGrpId])
        return _.first(usrGrps)
    }

    static async getAppExec(dbContext: PAdbContext, appExecId: string): Promise<WB_UIAppExecutableRow> {
        if(_.isEmpty(appExecId))
            return undefined

        const appExecs = <WB_UIAppExecutableRow[]> await dbContext.findAll(esWB_UI_AppExecutable, {
            where: {APPEXEC_ID: appExecId, DELETED_IND: {neq: '1'}}})
        return _.first(appExecs)
    }

    static async getAppExecObjects(appExecId: string, options?: {
        wbIds?: number[],
        parentWbIds?: number[],
        wbMDSetId?: number,
        wbSalesOrgId?: number
    }): Promise<vUI_AppExec_Objects_Row[]>
    {
        let opts = options || {}
        if(_.isEmpty(appExecId))
            return []

        let cnd: any = {APPEXEC_ID: appExecId}
        if(_.isArray(opts.wbIds)){
            cnd.WB_ID = {inq: opts.wbIds}
        }

        if(_.isArray(opts.parentWbIds)){
            cnd.PARENT_WB_ID = {inq: opts.parentWbIds}
        }

        if(_.isNumber(opts.wbMDSetId) && (opts.wbMDSetId > 0)){
            cnd.WB_MDSET_ID = opts.wbMDSetId
        }

        if(_.isNumber(opts.wbSalesOrgId) && (opts.wbSalesOrgId > 0)){
            cnd.WB_SALESORG_ID = opts.wbSalesOrgId
        }

        let  rows: vUI_AppExec_Objects_Row[] = await PASearch.find("vUI_AppExec_Objects",
            {where: cnd}, vUI_AppExec_Objects_schema,[],0, [])

        return rows
    }

}